import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'

const BASE_URL = 'https://api.gateio.ws'
const BASE_PATH = '/api/v4'

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  method: 'GET' | 'POST'
  path: string
  queryString: string
  bodyString: string
}): Promise<Record<string, string>> => {
  const {
    apiKey,
    apiSecret,
    createHeaders,
    method,
    path,
    queryString,
    bodyString,
  } = params

  // timestamp の作成
  const timestamp = Math.floor(Date.now() / 1000).toString()

  // body を JSON 文字列化してから SHA512 でハッシュ化
  const bodyHashed = await crypto.subtle
    .digest('SHA-512', new TextEncoder().encode(bodyString))
    .then((hash) =>
      [...new Uint8Array(hash)]
        .map((x) => x.toString(16).padStart(2, '0'))
        .join(''),
    )

  // 署名対象文字列の作成: method + '\n' + path + '\n' + query + '\n' + bodyHashed + '\n' + timestamp
  const signTarget = `${method}\n${path}\n${queryString}\n${bodyHashed}\n${timestamp}`

  // HMAC-SHA512署名の生成
  const signature = await sign({
    algorithm: 'sha512',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'hex',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    KEY: apiKey,
    SIGN: signature,
    Timestamp: timestamp,
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const bodyString = body ? JSON.stringify(body) : ''
      const url = `${BASE_URL}${BASE_PATH}${path}${queryWithQuestionMark}`
      const headers = await createAuthHeaders({
        apiKey,
        apiSecret,
        createHeaders,
        method,
        path: `${BASE_PATH}${path}`,
        queryString,
        bodyString,
      })
      return {
        url,
        headers,
        body: method === 'POST' ? bodyString : undefined,
      }
    },
    getDelayForRetry: ({ statusCode }) => {
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
